import { Tooltip, TooltipTrigger } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import IconButton from '~/components/buttons/NewIconButton';
import Icon from '~/components/data-display/Icon';
import { HStack, VStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import ReviewFillIcon from '~/icons/icon-review-fill.svg?react';
import { BookkeepingEntry, BookkeepingTransaction } from '~/types/general-bookkeeping';
import { VATBookType, VATRegistration, VATRegistrationEntry } from '~/types/vat-bookkeeping';
import { formatFromISOToDisplay } from '~/utils/formatDate';
import BookkeepingTransactionEntriesPreviewTable, {
  BookkeepingTransactionPreviewEntry,
} from '~/views/bookkeeping/bank-transactions/BookkeepingTransactionEntriesPreviewTable';
import BookkeepingFixedAssetsPreview from '~/views/bookkeeping/cash-book/BookkeepingFixedAssetsPreview';
import BookkeepingInvoicesEntriesPreviewTable, {
  BookkeepingInvoicesPreviewEntry,
} from '~/views/bookkeeping/documents/BookkeepingInvoicesEntriesPreviewTable';
import styles from './GeneralBookkeepingCashBookTooltip.module.scss';
import { PreviewField } from './PreviewField';

interface GeneralBookkeepingCashBookTooltipProps {
  bookkeepingTransaction: BookkeepingTransaction;
}

type CashBookTooltipType = 'MOVEMENT_REGISTRATION' | 'VAT_REGISTRATION';

const getTooltipType = (bookkeepingTransaction: BookkeepingTransaction): CashBookTooltipType => {
  if (bookkeepingTransaction.vatRegistration?.vatBook) {
    return 'VAT_REGISTRATION';
  }
  return 'MOVEMENT_REGISTRATION';
};

export const getBookkeepingAccountIdForEntry = (
  vatRegistration: VATRegistration,
  vatCode: VATRegistrationEntry['vatCode'],
  bookkeepingAccount: VATRegistrationEntry['bookkeepingAccount'],
) => {
  const { vatBook, vatRegistrationBills } = vatRegistration;

  if (vatBook.bookType === VATBookType.Bills && vatRegistrationBills) {
    const matchingCode = vatRegistrationBills.vatCodes.find(({ vatCode: { id } }) => id === vatCode.id);

    if (matchingCode?.bookkeepingAccount.id) {
      return matchingCode.bookkeepingAccount.id;
    }
  }

  return bookkeepingAccount?.id ?? '';
};

const mapVATEntries = (vatRegistration: VATRegistration): BookkeepingInvoicesPreviewEntry[] =>
  vatRegistration.vatEntries.map(({ bookkeepingAccount, vatCode, taxableAmount, taxAmount, purchaseType }) => {
    const bookkeepingAccountId = getBookkeepingAccountIdForEntry(vatRegistration, vatCode, bookkeepingAccount);
    return {
      bookkeepingAccountId,
      vatCodeId: vatCode.id,
      vatPercentage: vatCode.vatPercentage.toString(),
      taxableAmount,
      taxAmount,
      purchaseType: purchaseType ?? null,
    };
  });

const mapMovementEntries = (entries: BookkeepingEntry[]): BookkeepingTransactionPreviewEntry[] =>
  entries.map(({ bookkeepingAccount, amount, counterpart, document, source }) => ({
    bookkeepingAccount,
    amount,
    counterpart: counterpart ?? undefined,
    document: document ?? undefined,
    source,
  }));

const GeneralBookkeepingCashBookTooltip = ({ bookkeepingTransaction }: GeneralBookkeepingCashBookTooltipProps) => {
  const { t } = useTranslation('bookkeeping', { keyPrefix: 'general_bookkeeping.cash_book.preview' });

  const type = getTooltipType(bookkeepingTransaction);
  const vatRegistration = bookkeepingTransaction.vatRegistration;

  const vatEntries = vatRegistration?.vatEntries ? mapVATEntries(vatRegistration) : [];
  const movementEntries = mapMovementEntries(bookkeepingTransaction.bookkeepingEntries);

  const fixedAssets = vatRegistration?.vatEntries.flatMap(({ fixedAssets }) => fixedAssets ?? []) ?? [];

  const formattedDate = formatFromISOToDisplay(bookkeepingTransaction.date);

  return (
    <TooltipTrigger delay={0} closeDelay={0}>
      <IconButton Icon={ReviewFillIcon} variant="ghost" size="large" className={styles.previewTrigger} />

      <Tooltip className={styles.tooltip} data-testid="cash-book-tooltip" placement="top end" crossOffset={-48}>
        <VStack gap={2}>
          <HStack gap={1} align="center">
            <Icon component={ReviewFillIcon} className={styles.titleIcon} />
            <Text variant="body1" weight="semibold">
              {t('title')}
            </Text>
          </HStack>

          <HStack gap={2} align="center" data-testid="cash-book-tooltip-metadata">
            {type === 'MOVEMENT_REGISTRATION' ? (
              <>
                <PreviewField label={t('description')} value={bookkeepingTransaction.description} />
                <PreviewField label={t('registration_date')} value={formattedDate} />
              </>
            ) : (
              <>
                <PreviewField label={t('book')} value={vatRegistration?.vatBook.name ?? '-'} />
                <PreviewField label={t('registration_date')} value={formattedDate} />
              </>
            )}
          </HStack>

          <div data-testid="cash-book-tooltip-table">
            {type === 'MOVEMENT_REGISTRATION' ? (
              <BookkeepingTransactionEntriesPreviewTable entries={movementEntries} />
            ) : (
              <BookkeepingInvoicesEntriesPreviewTable entries={vatEntries} book={vatRegistration?.vatBook} />
            )}
          </div>

          {type === 'VAT_REGISTRATION' && <BookkeepingFixedAssetsPreview fixedAssets={fixedAssets} />}
        </VStack>
      </Tooltip>
    </TooltipTrigger>
  );
};

export default GeneralBookkeepingCashBookTooltip;
